import React from 'react'
import { useEffect, useState } from 'react'
import { SWAN_STYLE_KEY_MAP, ScreenClassProvider } from '@vp/swan'
import { useSwanStyleKeys, useUserContext, LoggerProvider, useLogger, useUserContextTracker } from '@vp/ubik-context'
import { TrackingProvider } from '../../hooks/trackingContext'
import { CampaignProvider } from '@vp/campaign-callouts'
import TilesSection from './TilesSection'
import { MCS_REACT_TOOLS_SWAN_STYLES } from '@vp/mcs-react-tools'
import { SWAN_STYLE_KEYS as PRICING_SWAN_STYLE_KEYS } from '@vp/price-platform'
import { isDebugModeEnabled } from '../../utils/debugMode'
import { shouldShowPrice } from '../../utils/priceDisplay'
import { ContentfulDebugWrapper } from '../shared/ContentfulDebugWrapper'
import type { FragmentLogger } from '@vp/ubik-logging'
import type { AppProps } from '../../types/homepageFeatureTileRow'
export const HomepageFeatureTileRow = (props: AppProps) => {
  const [hashExists, setHashExists] = useState(false)
  useEffect(() => {
    setHashExists(typeof window !== 'undefined' && window.location.hash !== '')
  }, [])

  const showPrice = shouldShowPrice(props.enablePrice, props.tileRowData?.tiles)

  const userContext = useUserContext()
  useSwanStyleKeys([
    SWAN_STYLE_KEY_MAP.core,
    SWAN_STYLE_KEY_MAP.grid,
    SWAN_STYLE_KEY_MAP.carousel,
    SWAN_STYLE_KEY_MAP.adaptiveCarousel,
    SWAN_STYLE_KEY_MAP.standardTile,
    SWAN_STYLE_KEY_MAP.utility,
    ...(isDebugModeEnabled(userContext) ? MCS_REACT_TOOLS_SWAN_STYLES : []),
    // Only pull in Price Platform's stylesheets when a price will actually be rendered.
    ...(showPrice ? PRICING_SWAN_STYLE_KEYS : []),
  ])

  const logger = useLogger() as FragmentLogger
  const tracker = useUserContextTracker()

  logger.setContext({
    ...props,
    name: 'feature-tile-row-fragment',
  })

  logger.info('Rendering Feature Tile Row Fragment')

  trackUsedContext(props, tracker)

  const search = typeof window !== 'undefined' ? window.location.search : ''

  const shouldRender = props.tileRowData && !!props.tileRowData.tiles?.length
  const tileList = props.tileRowData?.tiles

  return (
    <LoggerProvider>
      <ContentfulDebugWrapper
        contentfulMetadata={props.contentfulMetadata}
        availability={props.debugAvailability}
        search={search}
        explanation={props.explanation}
      >
        <TrackingProvider canonicalUrl="https://www.vistaprint.com/">
          <CampaignProvider tenant={props.tenant} locale={props.locale}>
            <ScreenClassProvider>
              {shouldRender && (
                <TilesSection
                  tileImageLoadingStrategy={props.eagerlyLoad || hashExists ? 'eager' : 'lazy'}
                  tileData={{ ...props.tileRowData, tileList }}
                  tileRow={props.tileRow}
                  tileTrackingZone={props.tileTrackingZone}
                  id={props.tileRowId}
                  enableCampaignCallouts={props.enableCampaignCallouts}
                  enablePrice={props.enablePrice}
                  gridToggle={props.gridToggle}
                  carouselA11yText={props.carouselA11yText}
                  locale={props.locale}
                />
              )}
            </ScreenClassProvider>
          </CampaignProvider>
        </TrackingProvider>
      </ContentfulDebugWrapper>
    </LoggerProvider>
  )
}

function trackUsedContext(props: AppProps, tracker: ReturnType<typeof useUserContextTracker>) {
  if (props.debugAvailability?.experimentKey && props.debugAvailability.variationKey) {
    tracker({
      type: 'experiment',
      name: props.debugAvailability.experimentKey,
      variation: props.debugAvailability.variationKey,
    })
  }
  if (props.debugAvailability?.decision) {
    tracker({
      type: 'treatment',
      name: props.debugAvailability.decision,
    })
  }
  if (props.debugAvailability?.merchandisingCampaign) {
    tracker({
      type: 'campaign',
      name: props.debugAvailability.merchandisingCampaign,
    })
  }
}
